/*
  Gaia Iluka — Where To Find Us: map interaction
  File: assets/gi-cw-map.js

  Scoped to sections/gi-cw-map.liquid. No framework, no external map
  library — the Korea outline is a static <path> already in the markup;
  this script only places markers on it and wires up selection, search
  and the experience filter.

  PROJECTION. sections/gi-cw-map.liquid's baked path was extracted with
  d3-geo's geoMercator().fitExtent([[40,78],[402,742]], korea) against a
  700×820 box. Reproducing d3's mercator formula in plain JS from the
  scale/translate that exact fitExtent produced was verified pixel-for-
  pixel against four real coordinates (Jeonju, Seoul, Busan, Jeju) before
  this file was written — zero deviation. SCALE/TRANSLATE below are
  those captured values, not estimates.

  DATA. Each active partner's fields are embedded server-side as its own
  <script type="application/json" data-gi-cw-partner-json> (see the
  section), one per partner, read here rather than fetched. The matching
  detail card for each partner is also pre-rendered server-side (via
  gi-cw-partner-card.liquid) and hidden; this script only shows the one
  that matches the current selection. It never builds partner HTML —
  every escape, every "only show this link if the URL is real" check,
  and the Naver Map URL all live in that one Liquid snippet, not
  duplicated here.

  SEARCH AND CITIES ARE DERIVED, NEVER HARD-CODED. The suggestion list is
  the set of distinct city values present in the partner payload, so a
  new partner created in Shopify Admin appears in the suggestions with no
  code change. The same is true of the experience filter: EAT and BUY are
  read off the existing `category` field (retail is BUY, everything else
  is EAT) rather than from a new metaobject field, and each button is
  only rendered when at least one active partner falls under it.

  KEYBOARD. Each marker is an SVG <g role="button" tabindex="0"> with
  its own aria-label. Enter and Space both select it, matching a native
  <button>. The selected-partner region carries aria-live="polite" on
  its own container in the markup, so a screen reader announces the
  change without this script managing an announcement separately.
*/
(function () {
  'use strict';

  var SCALE = 4208.43778366488;
  var TRANSLATE = [-9215.327246165096, 3243.9213297952138];

  function project(lon, lat) {
    var lambda = (lon * Math.PI) / 180;
    var phi = (lat * Math.PI) / 180;
    var x = TRANSLATE[0] + SCALE * lambda;
    var y = TRANSLATE[1] - SCALE * Math.log(Math.tan(Math.PI / 4 + phi / 2));
    return [x, y];
  }

  /* EAT / BUY from the category the metaobject already carries. Only
     `retail` is a place you buy the oil; restaurants, hotels and
     experiences are all places you eat or are served it. Keeping this
     as one derived line — rather than a new metaobject field — is what
     lets the two filters exist without adding data architecture. */
  function experienceOf(category) {
    return category === 'retail' ? 'buy' : 'eat';
  }

  /* Lowercased, trimmed, accent-insensitive enough for city names in
     both alphabets. Korean has no case, so toLowerCase is a no-op there
     and the comparison still works. */
  function norm(s) {
    return String(s == null ? '' : s).toLowerCase().trim();
  }

  function init(root) {
    var svg = root.querySelector('[data-gi-cw-svg]');
    var markerGroup = root.querySelector('[data-gi-cw-markers]');
    var leaderGroup = root.querySelector('[data-gi-cw-leaders]');
    var filtersEl = root.querySelector('[data-gi-cw-filters]');
    var countEl = root.querySelector('[data-gi-cw-count]');
    var hintEl = root.querySelector('[data-gi-cw-hint]');
    var emptyEl = root.querySelector('[data-gi-cw-empty]');
    var searchEl = root.querySelector('[data-gi-cw-search]');
    var suggestEl = root.querySelector('[data-gi-cw-suggest]');
    var noResEl = root.querySelector('[data-gi-cw-noresults]');
    var clearEl = root.querySelector('[data-gi-cw-clear]');
    var panelRegion = root.querySelector('[data-gi-cw-panel-region]');
    if (!svg || !markerGroup) return;

    var jsonNodes = root.querySelectorAll('[data-gi-cw-partner-json]');
    var partners = [];
    for (var i = 0; i < jsonNodes.length; i++) {
      try {
        var data = JSON.parse(jsonNodes[i].textContent);
        /* partner.system.id renders as a bare NUMBER through the | json
           filter but as a STRING through | escape in the markup's
           data-partner-id. Every comparison below is strict (indexOf,
           ===), so without this one coercion the number never matches
           the attribute: every marker stayed data-dim="true" with
           tabindex="-1", clicks and Enter/Space did nothing, and the
           panel never opened. */
        data.id = String(data.id);
        data.xy = project(data.lon, data.lat);
        data.exp = experienceOf(data.category);
        /* One pre-lowercased haystack per partner: name, city, region
           and the translated category label. Built once so typing does
           not re-lowercase every field on every keystroke. */
        data.hay = [data.name, data.city, data.region, data.categoryLabel]
          .filter(Boolean).map(norm).join(' | ');
        partners.push(data);
      } catch (e) {
        /* one bad JSON block should not take down the whole map */
      }
    }
    if (!partners.length || !leaderGroup) return;

    var state = { exp: 'all', q: '', selected: null };
    var SVGNS = 'http://www.w3.org/2000/svg';

    function labelFor(p) {
      return p.name + ', ' + p.city;
    }

    /* Distinct cities, in the order the partners are emitted (sort_order,
       then name), so the suggestion row follows the same sequence the
       directory does. Derived from data — nothing about Seoul, Jeonju or
       any other city is written into this file. */
    function cities() {
      var seen = {}, out = [];
      partners.forEach(function (p) {
        var c = p.city;
        if (!c || seen[norm(c)]) return;
        seen[norm(c)] = 1;
        out.push(c);
      });
      return out;
    }

    /*
      LEADER LABELS.

      Every partner name is parked in the empty column to the right of the
      coastline and joined back to its own dot by a fine line, the way an
      atlas annotates a map. Nothing about this is per-partner: positions
      are derived from the projected coordinates at render time, so a new
      partner created in Shopify Admin gets a correctly placed label with
      no code change and no extra metaobject fields.

      Collisions are resolved by the pass below rather than by hand-tuned
      offsets. Labels want to sit level with their own dot; where two
      would overlap, the whole column is spread out from its centre until
      every entry clears MIN_GAP.

      Both lines are always drawn. The meta line is revealed by CSS only
      for the selected partner, rather than being added and removed here,
      so selecting a partner never re-runs the layout and no label ever
      jumps position. The gap below is therefore sized for the two-line
      case at every size, whether or not that partner is currently the
      selected one.
    */
    /* The clamp the declutter pass spreads labels between. These track
       the section's viewBox (0 144 580 532) with 12 units of inset, so a
       label pushed to either end of a long stack still sits inside the
       frame rather than being cropped by it. */
    var VB_TOP = 156, VB_BOTTOM = 664;

    /*
      The whole map is one SVG scaled to the column, so viewBox units and
      rendered pixels are only ever related by that scale. On a 390px
      screen the scale falls to about 0.60 and on a 320px screen to 0.48,
      which is what drove the secondary line down to illegible sizes.

      Type is therefore sized in steps that grow as the column narrows
      (see the matching font-size rules in the section's stylesheet), and
      the geometry those steps need travels with them here. The column
      start moves inboard on the narrowest screens because at that size a
      gutter beginning at x=418 is too thin to hold a partner name.

      Nothing here is per-partner: these are properties of the viewport,
      applied to whatever partners happen to be on the map.
    */
    function metrics() {
      var w = window.innerWidth;
      /* Below 480 the meta line is not drawn at all (see the section's
         stylesheet), so the gap only has to clear one line of 28px type
         — which is what lets more partners keep their own row before the
         declutter pass starts spreading them. */
      if (w <= 479) return { x: 322, gap: 46, l1: 26 };
      if (w <= 749) return { x: 410, gap: 46, l1: 18 };
      return { x: 418, gap: 38, l1: 15 };
    }

    function layoutLabels(list, M) {
      if (!list.length) return [];
      var MIN_GAP = M.gap;
      var items = list.map(function (p) {
        return { p: p, x: p.xy[0], y: p.xy[1], labelY: p.xy[1] };
      }).sort(function (a, b) { return a.y - b.y; });

      /* push down through the stack, then clamp back up from the bottom:
         two passes settle any run of overlapping labels */
      for (var i = 1; i < items.length; i++) {
        if (items[i].labelY - items[i - 1].labelY < MIN_GAP) {
          items[i].labelY = items[i - 1].labelY + MIN_GAP;
        }
      }
      var last = items[items.length - 1];
      if (last.labelY > VB_BOTTOM) {
        last.labelY = VB_BOTTOM;
        for (var j = items.length - 2; j >= 0; j--) {
          if (items[j + 1].labelY - items[j].labelY < MIN_GAP) {
            items[j].labelY = items[j + 1].labelY - MIN_GAP;
          }
        }
      }
      if (items[0].labelY < VB_TOP) {
        var shift = VB_TOP - items[0].labelY;
        items.forEach(function (it) { it.labelY += shift; });
      }
      return items;
    }

    /* Out from the dot, one elbow, then a short run into the label. A
       single right angle rather than a dogleg — where the label already
       sits level with its own dot the line stays perfectly straight. */
    function leaderPath(x, y, labelY, LABEL_X) {
      var midX = x + (LABEL_X - x) * 0.46;
      var start = x + 10;
      var end = LABEL_X - 10;
      if (Math.abs(labelY - y) < 0.5) {
        return 'M' + start + ' ' + y + 'H' + end;
      }
      return 'M' + start + ' ' + y + 'H' + midX + 'V' + labelY + 'H' + end;
    }

    function buildMarkers() {
      markerGroup.textContent = '';
      leaderGroup.textContent = '';
      var M = metrics();
      var LABEL_X = M.x;

      layoutLabels(partners, M).forEach(function (item) {
        var p = item.p, x = item.x, y = item.y;

        var lg = document.createElementNS(SVGNS, 'g');
        lg.setAttribute('class', 'gi-cw-leader');
        lg.setAttribute('data-id', p.id);
        lg.setAttribute('aria-hidden', 'true');

        var line = document.createElementNS(SVGNS, 'path');
        line.setAttribute('class', 'gi-cw-leader-line');
        line.setAttribute('d', leaderPath(x, y, item.labelY, LABEL_X));
        lg.appendChild(line);

        /* A short vertical tick where the leader meets its label — the
           terminal serif of the annotation. Only visible on the selected
           partner, where it is what makes the line read as pointing INTO
           the name rather than just stopping near it. */
        var tick = document.createElementNS(SVGNS, 'path');
        tick.setAttribute('class', 'gi-cw-leader-tick');
        tick.setAttribute('d', 'M' + (LABEL_X - 10) + ' ' + (item.labelY - 5) + 'v10');
        lg.appendChild(tick);

        var name = document.createElementNS(SVGNS, 'text');
        name.setAttribute('class', 'gi-cw-leader-name');
        name.setAttribute('x', LABEL_X);
        name.setAttribute('y', item.labelY);
        name.textContent = p.name;
        lg.appendChild(name);

        /* City and category on one line, not two. Three stacked lines per
           partner was the single biggest source of the "many small labels
           competing equally" reading; one quiet line under the name says
           the same thing. */
        var metaBits = [];
        if (p.city) metaBits.push(p.city);
        if (p.categoryLabel) metaBits.push(p.categoryLabel);
        if (metaBits.length) {
          var meta = document.createElementNS(SVGNS, 'text');
          meta.setAttribute('class', 'gi-cw-leader-meta');
          meta.setAttribute('x', LABEL_X);
          meta.setAttribute('y', item.labelY + M.l1);
          meta.textContent = metaBits.join('  ·  ');
          lg.appendChild(meta);
        }

        leaderGroup.appendChild(lg);

        var g = document.createElementNS(SVGNS, 'g');
        g.setAttribute('class', 'gi-cw-marker');
        g.setAttribute('role', 'button');
        g.setAttribute('tabindex', '0');
        g.setAttribute('aria-label', labelFor(p));
        g.setAttribute('aria-pressed', 'false');
        g.setAttribute('data-id', p.id);
        g.setAttribute('data-cat', p.category);
        g.setAttribute('data-exp', p.exp);

        var ring = document.createElementNS(SVGNS, 'circle');
        ring.setAttribute('class', 'gi-cw-marker-ring');
        ring.setAttribute('cx', x); ring.setAttribute('cy', y); ring.setAttribute('r', 11);
        g.appendChild(ring);

        var hit = document.createElementNS(SVGNS, 'circle');
        hit.setAttribute('class', 'gi-cw-marker-hit');
        hit.setAttribute('cx', x); hit.setAttribute('cy', y); hit.setAttribute('r', 18);
        g.appendChild(hit);

        var dot = document.createElementNS(SVGNS, 'circle');
        dot.setAttribute('class', 'gi-cw-marker-dot');
        dot.setAttribute('cx', x); dot.setAttribute('cy', y); dot.setAttribute('r', 4);
        g.appendChild(dot);

        /* Hovering a dot brings up that partner's own label without
           selecting it, so the map can be read by moving across it. The
           flag goes on the leader group because the label lives there;
           dimmed markers never raise it. */
        function hover(on) {
          if (g.getAttribute('data-dim') === 'true') return;
          lg.setAttribute('data-hover', on ? 'true' : 'false');
        }
        g.addEventListener('mouseenter', function () { hover(true); });
        g.addEventListener('mouseleave', function () { hover(false); });
        g.addEventListener('focus', function () { hover(true); });
        g.addEventListener('blur', function () { hover(false); });

        g.addEventListener('click', function () {
          if (g.getAttribute('data-dim') === 'true') return;
          select(p.id);
        });
        g.addEventListener('keydown', function (evt) {
          if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
            evt.preventDefault();
            if (g.getAttribute('data-dim') === 'true') return;
            select(p.id);
          }
        });

        markerGroup.appendChild(g);
      });
    }

    /*
      GALLERY.

      Arrows and a fraction only; the scroll-snap track does the actual
      moving, so swipe and trackpad work whether or not this runs. Bound
      per panel card and re-bound after a selection change, because only
      the selected partner's card is in the document flow at any time.

      Markup guarantees there is nothing to bind unless a partner has two
      or more images, so there is no empty-state branch to get wrong here.
    */
    function initGallery(card) {
      var gallery = card.querySelector('[data-gi-cw-gallery]');
      if (!gallery || gallery.getAttribute('data-gi-ready') === 'true') return;
      var track = gallery.querySelector('[data-gi-cw-track]');
      var prev = gallery.querySelector('[data-gi-cw-prev]');
      var next = gallery.querySelector('[data-gi-cw-next]');
      var fracEl = gallery.querySelector('[data-gi-cw-frac]');
      if (!track) return;
      var slides = track.querySelectorAll('.gi-cw-card__slide');
      if (slides.length < 2 || !prev || !next) return;
      gallery.setAttribute('data-gi-ready', 'true');

      function index() {
        var w = track.clientWidth || 1;
        return Math.round(track.scrollLeft / w);
      }
      function sync() {
        var i = index();
        prev.disabled = i <= 0;
        next.disabled = i >= slides.length - 1;
        /* Digits only — no word to translate, correct in every locale. */
        if (fracEl) fracEl.textContent = (i + 1) + ' / ' + slides.length;
      }
      function go(dir) {
        track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
      }

      prev.addEventListener('click', function () { go(-1); });
      next.addEventListener('click', function () { go(1); });
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { sync(); ticking = false; });
      });
      window.addEventListener('resize', sync);
      sync();
    }

    /* Label geometry is viewport-dependent, so it is rebuilt when the
       window crosses a step. Debounced, and it re-applies the current
       filter and selection afterwards so a resize never silently drops
       the dim state or the selected marker. */
    var resizeTimer = null;
    var lastStep = metrics().x + ':' + metrics().gap;
    window.addEventListener('resize', function () {
      var step = metrics().x + ':' + metrics().gap;
      if (step === lastStep) return;
      lastStep = step;
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        buildMarkers();
        updateMarkers();
        updateFilters();
      }, 150);
    });

    /* One control: move the reader to the directory below. No overlay,
       nothing to dismiss, and it degrades to nothing if the directory is
       not on the page. */
    var viewList = root.querySelector('[data-gi-cw-viewlist]');
    if (viewList) {
      var directory = document.querySelector('.gi-cw-dir');
      if (!directory) {
        viewList.hidden = true;
      } else {
        viewList.addEventListener('click', function () {
          directory.scrollIntoView({ behavior: 'smooth', block: 'start' });
          var h = directory.querySelector('.gi-cw-dir__heading');
          if (h) {
            h.setAttribute('tabindex', '-1');
            h.focus({ preventScroll: true });
          }
        });
      }
    }

    /* City search + experience filter combine: a partner has to satisfy
       both to stay on the map. Matching is a substring test against the
       pre-built haystack, so "seoul", "Seoul", "jeonju" and a partner's
       own name all work, in either locale. */
    function visiblePartners() {
      var q = norm(state.q);
      return partners.filter(function (p) {
        if (state.exp !== 'all' && p.exp !== state.exp) return false;
        if (q && p.hay.indexOf(q) === -1) return false;
        return true;
      });
    }

    function updateFilters() {
      if (filtersEl) {
        var buttons = filtersEl.querySelectorAll('[data-gi-cw-filter]');
        for (var i = 0; i < buttons.length; i++) {
          var btn = buttons[i];
          btn.setAttribute('aria-pressed',
            btn.getAttribute('data-gi-cw-filter') === state.exp ? 'true' : 'false');
        }
      }
      var n = visiblePartners().length;
      if (countEl) {
        var one = countEl.getAttribute('data-count-one');
        var other = countEl.getAttribute('data-count-other');
        if (n === 1 && one) {
          countEl.textContent = one;
        } else if (other) {
          countEl.textContent = other.replace('__N__', n);
        } else {
          /* pre-translation fallback, English only */
          countEl.textContent = n + (n === 1 ? ' partner' : ' partners');
        }
      }
      /* The zero state belongs beside the search that produced it, not
         in the partner panel — the panel keeps saying "select a
         location", which stays true. The reset control lives inside this
         paragraph, so its visibility is this one line and there is no
         second hidden-state to keep in sync. */
      if (noResEl) noResEl.hidden = n !== 0;
    }

    /* Suggestions are the distinct cities in the data, narrowed to those
       that still match what has been typed. Rendered inline under the
       field — no floating panel, nothing to dismiss, no z-index. */
    function updateSuggestions() {
      if (!suggestEl) return;
      var q = norm(state.q);
      var list = cities().filter(function (c) {
        return !q || norm(c).indexOf(q) !== -1;
      });
      /* Nothing to offer once the query already names the only city. */
      if (list.length < 2 && !q) list = cities();
      suggestEl.textContent = '';
      list.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'gi-cw-map__city';
        b.setAttribute('data-gi-cw-city', c);
        b.setAttribute('aria-pressed', norm(state.q) === norm(c) ? 'true' : 'false');
        b.textContent = c;
        suggestEl.appendChild(b);
      });
      suggestEl.hidden = list.length === 0;
    }

    function updateMarkers() {
      var visible = visiblePartners();
      var visibleIds = visible.map(function (p) { return p.id; });
      var markers = markerGroup.querySelectorAll('.gi-cw-marker');
      for (var i = 0; i < markers.length; i++) {
        var m = markers[i];
        var id = m.getAttribute('data-id');
        var dim = visibleIds.indexOf(id) === -1;
        var sel = id === state.selected;
        m.setAttribute('data-dim', dim ? 'true' : 'false');
        m.setAttribute('tabindex', dim ? '-1' : '0');
        m.setAttribute('aria-pressed', sel ? 'true' : 'false');

        /* the leader and its label follow the marker they belong to */
        var leader = leaderGroup.querySelector('.gi-cw-leader[data-id="' + id + '"]');
        if (leader) {
          leader.setAttribute('data-dim', dim ? 'true' : 'false');
          leader.setAttribute('data-selected', sel ? 'true' : 'false');
        }
      }
    }

    function updatePanel() {
      var panels = root.querySelectorAll('[data-gi-cw-panel]');
      var matched = false;
      for (var i = 0; i < panels.length; i++) {
        var isMatch = panels[i].getAttribute('data-partner-id') === state.selected;
        panels[i].hidden = !isMatch;
        if (isMatch) { matched = true; initGallery(panels[i]); }
      }
      if (emptyEl) emptyEl.hidden = matched;
      if (hintEl) hintEl.hidden = matched;
      /* The panel's top rule turns accent while a partner is selected —
         the same accent the selected leader line uses, so the map's
         pointing line and the panel it points at read as one gesture. */
      if (panelRegion) panelRegion.setAttribute('data-selected', matched ? 'true' : 'false');
    }

    /* Selecting is the one place state.selected is written, so the
       "a filter or a search dropped the selected partner" rule below
       only has to exist once. */
    function select(id) {
      state.selected = id;
      updateMarkers();
      updatePanel();
    }

    /* After any change to the filter or the query: a search that lands on
       exactly one partner selects it, because that is what the reader
       asked for and the panel is where the answer lives. A search that
       lands on several keeps whatever was already selected if it is still
       among them, and otherwise clears rather than guessing. */
    function reconcile() {
      var visible = visiblePartners();
      if (state.selected && !visible.some(function (p) { return p.id === state.selected; })) {
        state.selected = null;
      }
      if (visible.length === 1) state.selected = visible[0].id;
      updateFilters();
      updateSuggestions();
      updateMarkers();
      updatePanel();
    }

    function setExperience(exp) {
      state.exp = exp;
      reconcile();
    }
    function setQuery(q) {
      state.q = q;
      reconcile();
    }

    if (filtersEl) {
      filtersEl.addEventListener('click', function (evt) {
        var btn = evt.target.closest('[data-gi-cw-filter]');
        if (!btn) return;
        setExperience(btn.getAttribute('data-gi-cw-filter'));
      });
    }

    if (searchEl) {
      searchEl.addEventListener('input', function () { setQuery(searchEl.value); });
      /* Enter must not submit anything — there is no form and no results
         page; the map already updated on the keystroke before it. */
      searchEl.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter') evt.preventDefault();
      });
    }

    if (suggestEl) {
      suggestEl.addEventListener('click', function (evt) {
        var btn = evt.target.closest('[data-gi-cw-city]');
        if (!btn) return;
        var city = btn.getAttribute('data-gi-cw-city');
        /* A second press on the active city clears it, so the row works
           as a toggle rather than a one-way trap. */
        var next = norm(state.q) === norm(city) ? '' : city;
        if (searchEl) searchEl.value = next;
        setQuery(next);
      });
    }

    if (clearEl) {
      clearEl.addEventListener('click', function () {
        if (searchEl) searchEl.value = '';
        state.q = '';
        state.exp = 'all';
        reconcile();
        if (searchEl) searchEl.focus();
      });
    }

    buildMarkers();
    /* Initial pass goes through reconcile so a single-partner shop opens
       with that partner already selected and the panel already showing
       its story, rather than on an empty "select a location". */
    reconcile();
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('.gi-cw-map');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (evt) {
    boot(evt.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    /* markers are rebuilt from the JSON payload on the next load event;
       nothing external to clean up (no observers, no timers). */
  });
})();
